import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { api } from '../utils/api';
import { MapPin, Loader2, Check, ChevronRight, ChevronLeft } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const CATEGORY_LABELS = {
  broadband_only: 'Broadband Only',
  broadband_iptv: 'Broadband + IPTV',
  broadband_ott: 'Broadband + OTT',
  broadband_iptv_ott: 'Broadband + IPTV + OTT (Complete Package)'
};

export const BroadbandLeadForm = () => {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [formData, setFormData] = useState({
    // Customer Info
    full_name: '',
    mobile: '',
    whatsapp: '',
    email: '',
    
    // Plan Selection
    plan_id: '',
    billing_cycle: 'yearly', // Fixed to yearly only
    
    // Address
    address: {
      latitude: null,
      longitude: null,
      accuracy: null,
      formatted_address: '',
      street: '',
      area: '',
      city: '',
      state: '',
      pincode: '',
      landmark: ''
    },
    
    // Preferences
    preferred_date: '',
    time_slot: '',
    notes: '',
    
    // Consent
    consent: false
  });
  const [submittedLead, setSubmittedLead] = useState(null);

  useEffect(() => {
    fetchPlans();
    captureUTMParams();
  }, []);

  const fetchPlans = async () => {
    try {
      const data = await api.getPlans();
      setPlans(data);
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to load plans. Please refresh the page.',
        variant: 'destructive'
      });
    }
  };

  const captureUTMParams = () => {
    const params = new URLSearchParams(window.location.search);
    const utm = {
      page_url: window.location.href,
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_term: params.get('utm_term'),
      utm_content: params.get('utm_content')
    };
    setFormData(prev => ({ ...prev, ...utm }));
  };

  const getLocation = () => {
    if (!navigator.geolocation) {
      toast({
        title: 'Error',
        description: 'Geolocation is not supported by your browser',
        variant: 'destructive'
      });
      return;
    }

    setGettingLocation(true);
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        
        try {
          // Reverse geocoding using Nominatim (free OSM service)
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
          );
          const data = await response.json();
          
          const address = data.address || {};
          
          setFormData(prev => ({
            ...prev,
            address: {
              latitude,
              longitude,
              accuracy,
              formatted_address: data.display_name || '',
              street: address.road || '',
              area: address.suburb || address.neighbourhood || '',
              city: address.city || address.town || address.village || '',
              state: address.state || '',
              pincode: address.postcode || '',
              landmark: ''
            }
          }));
          
          toast({
            title: 'Location Captured',
            description: 'Your location has been successfully captured'
          });
        } catch (error) {
          toast({
            title: 'Warning',
            description: 'Location captured but address lookup failed. Please enter manually.',
            variant: 'destructive'
          });
          
          setFormData(prev => ({
            ...prev,
            address: {
              ...prev.address,
              latitude,
              longitude,
              accuracy
            }
          }));
        } finally {
          setGettingLocation(false);
        }
      },
      (error) => {
        setGettingLocation(false);
        toast({
          title: 'Error',
          description: 'Unable to get location. Please enter address manually.',
          variant: 'destructive'
        });
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.consent) {
      toast({
        title: 'Consent Required',
        description: 'Please agree to the terms and conditions',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);

    try {
      // Prepare data - convert empty strings to null for optional fields
      const submitData = {
        ...formData,
        whatsapp: formData.whatsapp || null,
        email: formData.email || null,
        preferred_date: formData.preferred_date || null,
        time_slot: formData.time_slot || null,
        notes: formData.notes || null
      };
      
      const response = await api.submitLead(submitData);
      setSubmittedLead(response);
      setStep(4);
      
      toast({
        title: 'Success!',
        description: 'Your broadband inquiry has been submitted successfully'
      });
    } catch (err) {
      toast({
        title: 'Submission Failed',
        description: err.message || 'Please try again later',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const groupedPlans = plans.reduce((acc, plan) => {
    if (!acc[plan.category]) {
      acc[plan.category] = [];
    }
    acc[plan.category].push(plan);
    return acc;
  }, {});

  const calculatePrice = (plan) => {
    const priceMap = {
      monthly: plan.monthly_price,
      quarterly: plan.quarterly_price,
      half_yearly: plan.half_yearly_price,
      yearly: plan.yearly_price
    };
    return priceMap[formData.billing_cycle] || plan.yearly_price;
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Select Your Plan</h2>
        <p className="text-gray-600">Choose the perfect plan for your needs (Annual Plans)</p>
      </div>

      {/* Plans by Category */}
      <div className="space-y-6">
        {Object.keys(groupedPlans).map((category) => (
          <div key={category}>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              {CATEGORY_LABELS[category]}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {groupedPlans[category].map((plan) => (
                <Card
                  key={plan.id}
                  className={`cursor-pointer transition-all ${
                    selectedPlan?.id === plan.id
                      ? 'border-2 border-red-600 bg-red-50'
                      : 'hover:border-red-300'
                  }`}
                  onClick={() => {
                    setSelectedPlan(plan);
                    setFormData({ ...formData, plan_id: plan.id });
                  }}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="font-bold text-lg">{plan.name}</h4>
                        <p className="text-sm text-gray-600">{plan.speed_mbps} Mbps</p>
                      </div>
                      {selectedPlan?.id === plan.id && (
                        <Check className="text-red-600" size={24} />
                      )}
                    </div>
                    <div className="text-2xl font-bold text-red-600">
                      ₹{plan.yearly_price.toLocaleString()}/year
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      ₹{plan.monthly_price}/month
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Button
        type="button"
        onClick={() => setStep(2)}
        disabled={!selectedPlan}
        className="w-full bg-red-600 hover:bg-red-700"
      >
        Continue <ChevronRight size={20} />
      </Button>
    </div>
  );

  // Step 2: Customer Info & Address
  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Your Information</h2>
        <p className="text-gray-600">Tell us about yourself and installation address</p>
      </div>

      {/* Customer Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="full_name">Full Name *</Label>
          <Input
            id="full_name"
            value={formData.full_name}
            onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
            required
            placeholder="John Doe"
          />
        </div>
        <div>
          <Label htmlFor="mobile">Mobile Number *</Label>
          <Input
            id="mobile"
            type="tel"
            pattern="[6-9][0-9]{9}"
            value={formData.mobile}
            onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
            required
            placeholder="98765 43210"
            maxLength={10}
          />
        </div>
        <div>
          <Label htmlFor="whatsapp">WhatsApp Number</Label>
          <Input
            id="whatsapp"
            type="tel"
            pattern="[6-9][0-9]{9}"
            value={formData.whatsapp}
            onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
            placeholder="98765 43210"
            maxLength={10}
          />
        </div>
        <div>
          <Label htmlFor="email">Email Address</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="john@example.com"
          />
        </div>
      </div>

      {/* Address Section */}
      <div>
        <Label className="text-lg font-semibold">Installation Address</Label>
        <Button
          type="button"
          onClick={getLocation}
          disabled={gettingLocation}
          variant="outline"
          className="mt-2 w-full border-red-600 text-red-600 hover:bg-red-50"
        >
          {gettingLocation ? (
            <><Loader2 className="animate-spin mr-2" size={20} /> Getting Location...</>
          ) : (
            <><MapPin className="mr-2" size={20} /> Use My Current Location</>
          )}
        </Button>
        
        {formData.address.latitude && (
          <p className="text-sm text-green-600 mt-2">
            ✓ Location captured ({formData.address.latitude.toFixed(6)}, {formData.address.longitude.toFixed(6)})
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <Label htmlFor="street">Street / Building</Label>
          <Input
            id="street"
            value={formData.address.street}
            onChange={(e) => setFormData({ 
              ...formData, 
              address: { ...formData.address, street: e.target.value }
            })}
            placeholder="Building name, street"
          />
        </div>
        <div>
          <Label htmlFor="area">Area / Locality</Label>
          <Input
            id="area"
            value={formData.address.area}
            onChange={(e) => setFormData({ 
              ...formData, 
              address: { ...formData.address, area: e.target.value }
            })}
            placeholder="Area name"
          />
        </div>
        <div>
          <Label htmlFor="city">City *</Label>
          <Input
            id="city"
            value={formData.address.city}
            onChange={(e) => setFormData({ 
              ...formData, 
              address: { ...formData.address, city: e.target.value }
            })}
            required
            placeholder="City"
          />
        </div>
        <div>
          <Label htmlFor="state">State</Label>
          <Input
            id="state"
            value={formData.address.state}
            onChange={(e) => setFormData({ 
              ...formData, 
              address: { ...formData.address, state: e.target.value }
            })}
            placeholder="State"
          />
        </div>
        <div>
          <Label htmlFor="pincode">Pincode</Label>
          <Input
            id="pincode"
            value={formData.address.pincode}
            onChange={(e) => setFormData({ 
              ...formData, 
              address: { ...formData.address, pincode: e.target.value }
            })}
            placeholder="380015"
            maxLength={6}
          />
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="landmark">Landmark</Label>
          <Input
            id="landmark"
            value={formData.address.landmark}
            onChange={(e) => setFormData({ 
              ...formData, 
              address: { ...formData.address, landmark: e.target.value }
            })}
            placeholder="Near..."
          />
        </div>
      </div>

      <div className="flex gap-4">
        <Button
          type="button"
          onClick={() => setStep(1)}
          variant="outline"
          className="flex-1"
        >
          <ChevronLeft size={20} /> Back
        </Button>
        <Button
          type="button"
          onClick={() => setStep(3)}
          className="flex-1 bg-red-600 hover:bg-red-700"
          disabled={!formData.full_name || !formData.mobile || !formData.address.city}
        >
          Continue <ChevronRight size={20} />
        </Button>
      </div>
    </div>
  );

  // Step 3: Consent & Submit
  const renderStep3 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Review & Submit</h2>
        <p className="text-gray-600">Confirm your details and submit inquiry</p>
      </div>

      {/* Order Summary */}
      <Card className="bg-gray-50">
        <CardHeader>
          <CardTitle className="text-lg">Inquiry Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <p className="text-sm text-gray-600 mb-2">Selected Plan</p>
            <div className="bg-white p-4 rounded border">
              <p className="font-bold text-lg">{selectedPlan?.name}</p>
              <p className="text-gray-600">{selectedPlan?.speed_mbps} Mbps - {CATEGORY_LABELS[selectedPlan?.category]}</p>
              <p className="text-2xl font-bold text-red-600 mt-2">
                Rs. {selectedPlan ? selectedPlan.yearly_price.toLocaleString() : 0}/year
              </p>
            </div>
          </div>
          
          <div>
            <p className="text-sm text-gray-600 mb-2">Contact Information</p>
            <div className="bg-white p-4 rounded border space-y-1">
              <p><strong>Name:</strong> {formData.full_name}</p>
              <p><strong>Mobile:</strong> {formData.mobile}</p>
              {formData.email && <p><strong>Email:</strong> {formData.email}</p>}
            </div>
          </div>

          <div>
            <p className="text-sm text-gray-600 mb-2">Installation Address</p>
            <div className="bg-white p-4 rounded border">
              <p>{formData.address.street && `${formData.address.street}, `}
                 {formData.address.area && `${formData.address.area}, `}
                 {formData.address.city}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Consent */}
      <div className="flex items-start space-x-2 bg-yellow-50 p-4 rounded-lg">
        <Checkbox
          id="consent"
          checked={formData.consent}
          onCheckedChange={(checked) => setFormData({ ...formData, consent: checked })}
        />
        <Label htmlFor="consent" className="text-sm leading-relaxed">
          I agree to be contacted by i5World regarding this broadband inquiry. I understand this is not a purchase order but an expression of interest.
        </Label>
      </div>

      <div className="flex gap-4">
        <Button
          type="button"
          onClick={() => setStep(2)}
          variant="outline"
          className="flex-1"
        >
          <ChevronLeft size={20} /> Back
        </Button>
        <Button
          type="submit"
          className="flex-1 bg-red-600 hover:bg-red-700"
          disabled={loading || !formData.consent}
        >
          {loading ? <Loader2 className="animate-spin" size={20} /> : 'Submit Inquiry'}
        </Button>
      </div>
    </div>
  );

  // Step 4: Success
  const renderStep4 = () => (
    <div className="text-center space-y-6 py-8">
      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
        <Check size={40} className="text-green-600" />
      </div>
      <h2 className="text-3xl font-bold text-gray-900">Inquiry Submitted!</h2>
      <div className="bg-gray-50 p-6 rounded-lg max-w-md mx-auto">
        <p className="text-sm text-gray-600 mb-2">Your Reference ID</p>
        <p className="text-2xl font-bold text-red-600">{submittedLead?.reference_id}</p>
        <p className="text-sm text-gray-600 mt-4">
          Please save this reference ID for future communication
        </p>
      </div>
      <p className="text-gray-700">
        Thank you for your interest in i5World! Our team will contact you within 24 hours to discuss your broadband requirements.
      </p>
      <Button
        onClick={() => window.location.href = '/'}
        className="bg-red-600 hover:bg-red-700"
      >
        Back to Home
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen pt-20 pb-12 bg-gradient-to-br from-red-50 via-white to-red-50">
      <div className="container mx-auto px-4 py-12">
        <Card className="max-w-4xl mx-auto">
          <CardHeader>
            <div className="flex items-center justify-between mb-4">
              <div className="flex space-x-2">
                {[1, 2, 3].map((s) => (
                  <div
                    key={s}
                    className={`h-2 flex-1 rounded-full ${
                      s <= step ? 'bg-red-600' : 'bg-gray-200'
                    }`}
                    style={{ width: '80px' }}
                  />
                ))}
              </div>
              {step < 4 && (
                <span className="text-sm text-gray-600">Step {step} of 3</span>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit}>
              {step === 1 && renderStep1()}
              {step === 2 && renderStep2()}
              {step === 3 && renderStep3()}
              {step === 4 && renderStep4()}
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
